import React from "react";
import { Users, LogOut, Trash2, Gamepad2 } from "lucide-react";
import ConversationOptionsButton from "./ConversationOptionsButton";

export default function ConversationHeader({
  isGroupChat,
  selectedGroup,
  selectedFriend,
  currentUserId,
  onlineFriends,
  selectedFriendId,
  typingUsers,
  groupTypingUsers,
  selectedGroupId,
  onLeaveGroup,
  onDisbandGroup,
  displayTitle,
  onOpenConversationSettings,
  onPlayGame,
  playGameDisabled,
}) {
  const title =
    displayTitle ||
    (isGroupChat ? selectedGroup?.name : selectedFriend?.username) ||
    "";

  return (
    <header className="px-7 py-4 border-b border-white/10 bg-[#0c121f]/75">
      <div className="flex items-center gap-3 min-w-0">
        {isGroupChat ? (
          <div className="h-11 w-11 rounded-2xl bg-white/[0.05] border border-white/12 grid place-items-center shadow-[0_8px_20px_rgba(0,0,0,0.24)]">
            <Users className="text-white/85" size={18} />
          </div>
        ) : selectedFriend?.profile_picture ? (
          <img
            src={selectedFriend.profile_picture}
            alt={selectedFriend.username}
            className="w-11 h-11 rounded-2xl object-cover border border-white/12 shadow-[0_8px_20px_rgba(0,0,0,0.24)]"
          />
        ) : (
          <div className="w-11 h-11 rounded-2xl bg-white/10 border border-white/12 grid place-items-center text-white font-semibold shadow-[0_8px_20px_rgba(0,0,0,0.24)]">
            {selectedFriend?.username?.[0]?.toUpperCase() || "?"}
          </div>
        )}

        <div className="min-w-0 flex-1">
          <div className="text-white font-semibold text-[15.5px] tracking-tight truncate">{title}</div>
          <div className="mt-0.5 text-[12px] text-white/50 flex items-center gap-2">
            {isGroupChat ? (
              <>
                <span className="text-white/55">
                  {selectedGroup.member_count || selectedGroup.members?.length || 0} members
                </span>
                {groupTypingUsers[selectedGroupId]?.size > 0 && (
                  <span className="text-white/62">
                    • {Array.from(groupTypingUsers[selectedGroupId]).join(", ")} typing…
                  </span>
                )}
              </>
            ) : (
              <>
                <span
                  className={`inline-flex items-center gap-1 ${
                    onlineFriends.has(selectedFriendId) ? "text-[#9ce7d7]" : "text-white/45"
                  }`}
                >
                  <span
                    className={`inline-block h-1.5 w-1.5 rounded-full ${
                      onlineFriends.has(selectedFriendId) ? "bg-[#7be0ca]" : "bg-white/25"
                    }`}
                  />
                  {onlineFriends.has(selectedFriendId) ? "Online" : "Offline"}
                </span>
                {typingUsers.has(selectedFriendId) && <span className="text-white/62">• typing…</span>}
              </>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0 ml-auto">
          {isGroupChat && selectedGroup && (
            <>
              {selectedGroup.created_by === currentUserId ? (
                <button
                  onClick={onDisbandGroup}
                  className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-xl border border-red-400/20 bg-red-500/10 hover:bg-red-500/20 transition focus:outline-none focus:ring-2 focus:ring-red-400/30"
                >
                  <Trash2 className="text-red-300" size={14} />
                  <span className="text-[11px] font-medium text-red-200 hidden sm:inline">Disband</span>
                </button>
              ) : (
                <button
                  onClick={onLeaveGroup}
                  className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.06] transition focus:outline-none focus:ring-2 focus:ring-white/20"
                >
                  <LogOut className="text-white/70" size={14} />
                  <span className="text-[11px] font-medium text-white/70 hidden sm:inline">Leave</span>
                </button>
              )}
            </>
          )}

          {typeof onPlayGame === "function" && (
            <button
              type="button"
              onClick={onPlayGame}
              disabled={!!playGameDisabled}
              title={playGameDisabled ? "Connecting to games…" : "Play a game with this friend"}
              aria-label={playGameDisabled ? "Play game, connecting" : "Play game with friend"}
              className="inline-flex items-center justify-center gap-1.5 h-8 px-3 rounded-xl border border-emerald-400/15 bg-emerald-500/10 hover:bg-emerald-500/18 disabled:opacity-40 disabled:cursor-not-allowed transition focus:outline-none focus:ring-2 focus:ring-emerald-400/25"
            >
              <Gamepad2 className="text-emerald-200/90" size={14} />
              <span className="text-[11px] font-medium text-emerald-100/90 hidden sm:inline">Play game</span>
            </button>
          )}

          <ConversationOptionsButton onClick={onOpenConversationSettings} />
        </div>
      </div>
    </header>
  );
}
